export type WireInterval = 'month' | 'year';
/**
 * `fee` is a standalone one-time charge — it grants no plan, so paying it changes nothing.
 * `historical` is a past invoice a super-admin uploaded as a PDF: always paid, changes nothing.
 */
export type InvoiceType = 'initial' | 'renewal' | 'change' | 'fee' | 'historical';
export type InvoiceStatus = 'pending' | 'paid' | 'void' | 'expired';

export type InvoiceLineKind = 'plan' | 'integration' | 'card_fee' | 'discount' | 'custom';

/** One line on an invoice. `recurring: false` marks a one-time charge that never renews. */
export interface InvoiceLineItem {
  kind: InvoiceLineKind;
  description: string;
  amount: number;
  recurring: boolean;
}

export type QuoteStatus = 'new' | 'contacted' | 'won' | 'lost';

/** An Enterprise-plan quote request, as returned by the billing API. */
export interface QuoteRequest {
  _id: string;
  vendor: string;
  vendorName: string;
  vendorEmail?: string | null;
  plan?: string | null;
  planName?: string | null;
  selectedFeatures: string[];
  paymentMethod?: 'wire' | 'stripe';
  visualizationQuality?: 'low' | 'medium' | 'high' | null;
  volumes?: Record<string, number>;
  message?: string | null;
  status: QuoteStatus;
  handledBy?: string | null;
  adminNote?: string | null;
  quotedAmount?: number | null;
  quotedCurrency?: string | null;
  quotedInterval?: 'month' | 'year' | null;
  /** One-time integration fee for this deal. null inherits the plan's; 0 waives it. */
  integrationFee?: number | null;
  stripeProductId?: string | null;
  stripePriceId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Payload the configurator submits to request a custom quote. */
export interface CreateQuoteRequestPayload {
  selectedFeatures: string[];
  volumes?: Record<string, number>;
  message?: string;
  planId?: string;
  paymentMethod?: 'wire' | 'stripe';
  visualizationQuality?: 'low' | 'medium' | 'high';
}

/** Fields a super-admin can update on a quote request. */
export interface UpdateQuotePayload {
  status: QuoteStatus;
  note?: string;
  quotedAmount?: number;
  quotedCurrency?: string;
  quotedInterval?: 'month' | 'year';
  /** Send 0 to waive the plan's integration fee; omit to leave it as-is. */
  integrationFee?: number;
}

/** A wire-transfer (manual) invoice, as returned by the billing API. */
export interface VendorInvoice {
  _id: string;
  vendor: string;
  plan: string;
  planName: string;
  interval: WireInterval;
  /** The TOTAL due. `lineItems` break it down; older invoices have none. */
  amount: number;
  lineItems?: InvoiceLineItem[];
  currency: string;
  type: InvoiceType;
  status: InvoiceStatus;
  periodStart: string;
  periodEnd: string;
  issuedAt: string;
  dueDate: string;
  expiresAt?: string;
  paidAt?: string | null;
  confirmedBy?: string | null;
  reference?: string | null;
  invoiceNumber: string;
  createdAt?: string;
  updatedAt?: string;
}

/** A Stripe (card) invoice, as returned by GET /billing/my-stripe-invoices. */
export interface StripeInvoiceSummary {
  id: string;
  invoiceNumber: string;
  amount: number;
  currency: string;
  status: string; // paid | open | void | uncollectible
  issuedAt: string | null;
  paidAt: string | null;
  periodEnd: string | null;
  planName: string | null;
  interval: 'month' | 'year';
  /** One-time charges on this invoice (setup fees), so its total reconciles. */
  oneTimeLines?: Array<{ description: string; amount: number }>;
  pdfUrl: string | null;
  hostedUrl: string | null;
}

/** Free text that can be provided per language (e.g. { en, fr }). A plain string is legacy/single-language. */
export type LocalizedText = { [lang: string]: string };

/** Resolve localized free text for a language (locale → English → first non-empty). Accepts a legacy string. */
export function pickLocalizedText(value: string | LocalizedText | undefined, lang?: string): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const code = (lang || 'en').slice(0, 2);
  return value[code] || value.en || Object.values(value).find(Boolean) || '';
}

/** Company bank details shown on a wire invoice. */
export interface WireBankDetails {
  accountName?: string;
  bankName?: string;
  accountNumber?: string;
  iban?: string;
  swift?: string;
  routingNumber?: string;
  address?: string;
  instructions?: string | LocalizedText;
}

/** Seller branding rendered on the invoice PDF (super-admin configurable). */
export interface WireBranding {
  companyAddress?: string;
  taxId?: string;
  registrationNumber?: string;
  accentColor?: string;
  footerText?: string | LocalizedText;
}

/**
 * An invoice's lines, synthesizing the legacy single-line shape when absent —
 * mirrors invoice-lines.util on the API so both render the same breakdown.
 */
export function invoiceLines(inv: Pick<VendorInvoice, 'amount' | 'planName' | 'lineItems'>): InvoiceLineItem[] {
  if (inv?.lineItems?.length) return inv.lineItems;
  return [{ kind: 'plan', description: inv?.planName || '', amount: inv?.amount || 0, recurring: true }];
}

/** Global wire-billing settings (super-admin). */
export interface WireSettings {
  _id?: string;
  autoBilling: boolean;
  graceDays: number;
  initialExpiryDays: number;
  renewLeadDays: number;
  reminderLeadDays: number;
  /** Surcharge % added to card (Stripe) payments to cover processing fees (0 = disabled). */
  cardFeePercent?: number;
  /** Discount % applied to annual billing (0 = none). */
  annualDiscountPercent?: number;
  bankDetails: WireBankDetails;
  branding: WireBranding;
  invoiceNumberSeq?: number;
}
